// Progress wording and choices (spec 006 FR-056, 006 FR-057), the same as the
// apps' shared ProgressText, from i18n/common (spec 013 FR-010).
import { integer, t } from '../i18n/index.ts';

export type Measure = 'distance' | 'time' | 'elevation' | 'count';

// Weekly is a sport's measure per week for the last 13 weeks and the 13 before.
export interface Weekly {
  sport: string;
  measure: Measure;
  unit: 'm' | 's' | 'count';
  weeks: string[];
  this: number[];
  prior: number[];
  this_total: number;
  prior_total: number;
  change_pct?: number;
}

// measures are what a sport offers: a sport without a map (gps false) has no
// distance or elevation.
export function measures(gps: boolean): Measure[] {
  return gps ? ['distance', 'time', 'elevation', 'count'] : ['time', 'count'];
}

// MEASURE_LABELS name each measure, read from the catalog each time so they
// follow the language.
export const MEASURE_LABELS: Record<Measure, string> = Object.defineProperties(
  {} as Record<Measure, string>,
  Object.fromEntries((['distance', 'time', 'elevation', 'count'] as const).map((m) => [m, { enumerable: true, get: () => t('insightsMeasure', { measure: m }) }])),
);

// summary says in a sentence how the last 3 months compare with the 3 before.
// A change that rounds to 0% is the same.
export function summary(w: Weekly): string {
  if (w.this_total === 0 && w.prior_total === 0) return t('progressNothingLogged');
  if (w.change_pct == null) return t('progressNothingToCompare');
  const whole = Math.round(Math.abs(w.change_pct));
  if (whole === 0) return t('progressSame', { measure: w.measure });
  if (w.change_pct > 0) return t('progressMore', { measure: w.measure, pct: integer(whole) });
  return t('progressLess', { measure: w.measure, pct: integer(whole) });
}

// weekStart is the locale's first day of the week, 0 Sunday to 6 Saturday.
export function weekStart(language: string): number {
  try {
    const locale = new Intl.Locale(language) as Intl.Locale & { getWeekInfo?: () => { firstDay: number }; weekInfo?: { firstDay: number } };
    return ((locale.getWeekInfo?.() ?? locale.weekInfo)?.firstDay ?? 7) % 7;
  } catch {
    return 0;
  }
}
